import { createSlice, createAction, current, PayloadAction } from "@reduxjs/toolkit";
import { has, isArray, sortBy } from "lodash";
import { nullBucket } from "@/features/plans/classes/PlanUtils";

// Trigger action for saga compatibility (saga watches this)
export const fetchOrganizationPlanBuckets = createAction<any>(
  "organizationPlanBuckets/trigger"
);

// External action this slice responds to
import { logoutStart } from "@/store/slices/auth";

// Top level:
// {
//    loading: 0, // qty of active requests (0 when none)
//    data: {}    // data object keyed by org ID
// }

interface OrgBucketsEntry {
  loading: boolean;
  failed: boolean;
  buckets: any[];
}

interface OrganizationPlanBucketsState {
  loading?: number;
  data: Record<number, OrgBucketsEntry>;
}

const initialState: OrganizationPlanBucketsState = {
  data: {},
};

const organizationPlanBucketsSlice = createSlice({
  name: "organizationPlanBuckets",
  initialState,
  reducers: {
    // Payload must be numeric org ID.
    fetchOrganizationPlanBucketsStart(state, action: PayloadAction<any>) {
      const orgId = Number(action.payload);

      // Add org entry if not yet present.
      if (!has(state.data, orgId)) {
        state.data[orgId] = {
          loading: false,
          failed: false,
          buckets: [],
        };
      }

      // Add/increment loading flags.
      state.data[orgId].loading = true; // org-specific
      // top-level, not org-specific
      if (state.loading === undefined) {
        state.loading = 1;
      } else {
        state.loading++;
      }
    },
    fetchOrganizationPlanBucketsSuccess(state, action: PayloadAction<any>) {
      if (!isArray(action.payload.data)) {
        console.error(
          "Invalid payload.data in fetchOrganizationPlanBucketsSuccess.",
          action.payload.data
        );
        return;
      }

      // Extract org ID from the meta.path string.
      const path = action.payload.meta.path;
      const matches = path.match(
        /\/api\/v1\/organizations\/(\d+)\/plans\/current\/buckets/
      );
      const orgId = Number(matches[1]);

      // Sort em
      const sortedBuckets = sortBy(action.payload.data, ["weight"]);

      // Prepend the null bucket.
      sortedBuckets.unshift(nullBucket);

      state.loading = (state.loading || 1) - 1;
      state.data[orgId] = {
        loading: false,
        failed: false,
        buckets: sortedBuckets,
      };
    },
    fetchOrganizationPlanBucketsFailure(state, action: PayloadAction<any>) {
      console.error("fetchOrganizationPlanBucketsFailure", current(state), action.payload);
      state.loading = (state.loading || 1) - 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutStart, () => ({ ...initialState }));
  },
});

export const {
  fetchOrganizationPlanBucketsStart,
  fetchOrganizationPlanBucketsSuccess,
  fetchOrganizationPlanBucketsFailure,
} = organizationPlanBucketsSlice.actions;

export default organizationPlanBucketsSlice.reducer;
